import { reactive, toRefs, set } from 'vue'
import { clone } from '@/common/js/just'
import { Message } from 'view-design'
import { AUTO_ACQUISITION_STATUS, AUTO_ACQUISITION_STATUS_LIST, EMPTY_VALUE } from '../modify-auto-acquisition-batch/consts/index.js'
import { MEDIAS } from '@/common/js/constants.js'
import { batchEnableAutoAcquistionModify } from '@/api/convert.model'

export function useBatchAutoAcquisition(selectList, emit) {
  const state = reactive({
    autoAcquisitionModifyModal: false,
    batchModifyAutoAcquisitionRows: []
  })

  /**
   * @description: 批量一键起量
   */
  const showPlanAutoAcquisitionModal = () => {
    if (!selectList.value?.length) return Message.info('请选择至少一个计划！')
    state.autoAcquisitionModifyModal = true

    const rows = clone(selectList.value)
      .map((row) => {
        return {
          ...row,
          retryLoading: false,
          autoAcquisitionNewBudget: EMPTY_VALUE
        }
      })
      // 只支持【腾讯广告3.0】【腾讯广告1.0】和【头条2.0】，其余选择自动过滤掉
      .filter((row) => [MEDIAS.GDT, MEDIAS.TOUTIAO].includes(row.mediaId))

    state.batchModifyAutoAcquisitionRows = rows
  }

  const getAutoAcquisitionRowStatusText = (row) => {
    const statusItem = AUTO_ACQUISITION_STATUS_LIST.find(({ value }) => value === row.raiseStatus)
    return statusItem?.label || '未知'
  }

  const handleModifyRequest = (params, row) => {
    set(row, 'optLoading', true)
    batchEnableAutoAcquistionModify(params).then((data) => {
      const [newRow] = data || []
      if (newRow?.planId) {
        // 没有消息等于成功
        if (!newRow.msg) {
          set(row, 'raiseStatus', newRow.raiseStatusResult)
          emit('on-refresh')
        } else {
          Message.warning(newRow.msg)
        }
      }

      set(row, 'optLoading', false)
    })
  }

  const buildBasicParams = (row) => {
    const params = {
      planId: row.planId,
      mediaId: row.mediaId,
      projectId: row.projectIdForBudget,
      adId: row.adIdForBudget,
      raiseBudget: row.autoAcquisitionNewBudget
    }

    if (row.autoAcquisitionNewBudget > 0) params.raiseStatus = AUTO_ACQUISITION_STATUS.ENABLE
    if (row.autoAcquisitionNewBudget === 0) params.raiseStatus = AUTO_ACQUISITION_STATUS.CLOSED

    return params
  }

  /**
   * @description: 关闭、已完成、可预约、已预约的才允许设置起量预算
   * @param {*} row
   */
  const checkValidRow = (row) => {
    const { CLOSED, FINISHED, RESERVABLE, RESERVED } = AUTO_ACQUISITION_STATUS
    return [CLOSED, FINISHED, RESERVABLE, RESERVED].includes(row.raiseStatus)
  }

  /**
   * @description: 表格行样式,是否禁用当前行
   * @param {*} row
   */
  const getTableRowClassName = ({ row }) => {
    if (row.raiseStatus === AUTO_ACQUISITION_STATUS.ENABLE) return ''

    if (checkValidRow(row)) return ''

    return 'disabled-row'
  }

  /**
   * @description: 表格行校验
   * @return {*}
   */
  const tableValidate = (rows) => {
    let msg = ''
    let index = 0

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const budget = parseFloat(row.autoAcquisitionNewBudget)
      index = i

      // 忽略不能修改预算的行
      if (getTableRowClassName({ row }) === 'disabled-row') {
        continue
      }

      // 忽略已开启的行，如果设置了关闭一键起量，不需要校验，如果没有作任何修改，也不需要校验，接口提交会忽略emptyValue的行
      if (row.raiseStatus === AUTO_ACQUISITION_STATUS.ENABLE) {
        if (row.autoAcquisitionNewBudget === 0) continue
        if (row.autoAcquisitionNewBudget === EMPTY_VALUE) continue
      }

      if (isNaN(budget) || budget < 0) {
        msg = '请填写正确的预算值'
        break
      }

      if (row.mediaId === MEDIAS.GDT) {
        if (budget < 200 || budget > 100000) {
          msg = '预算区间应在200 ~ 100000之间'
          break
        }
      }

      if (row.mediaId === MEDIAS.TOUTIAO) {
        if (budget < 100) {
          msg = '一键起量预算大于等于100'
          break
        }
        if (budget > row.budget) {
          msg = '一键起量预算不能大于计划预算'
          break
        }
      }
    }

    return msg ? Promise.reject(new Error(JSON.stringify({ msg, index }))) : Promise.resolve()
  }

  const onAutoAcquisitionEnable = async (budget, row) => {
    try {
      row.autoAcquisitionNewBudget = budget
      await tableValidate([row])

      const params = buildBasicParams(row)

      handleModifyRequest({ task_list: [params] }, row)
    } catch (e) {
      const { msg } = JSON.parse(e.message) || {}
      msg && Message.error(msg)
    }
  }

  const onAutoAcquisitionClose = (budget, row) => {
    row.autoAcquisitionNewBudget = 0
    const params = buildBasicParams(row)

    handleModifyRequest({ task_list: [params] }, row)
  }

  return {
    ...toRefs(state),
    showPlanAutoAcquisitionModal,
    getAutoAcquisitionRowStatusText,
    onAutoAcquisitionEnable,
    onAutoAcquisitionClose,
    checkValidRow,
    getTableRowClassName,
    tableValidate,
    handleModifyRequest,
    buildBasicParams
  }
}
